import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { message } from "antd";
import HeaderBread from "../../../../components/HeaderBread";
import Layout from "../../../../Layout";
import Logs from "../../../../components/Logs";
import SwitchBar from "../../../../components/Pages/SwitchBar";
import SearchField from "../../../../components/SearchField";
import SortableTable from "../../../../components/UI/SortableTable";
import DeletePop from "../../../../constants/ErrorPermissionModal";
import { useDataLayerValue } from "../../../../DataLayer/DataLayer";
import { useDebounce } from "../../../../hooks";
import { checkPermission } from "../../../../utils/Validation/checkPermission";
import { addlog } from "../../../../utils/ApiCalls/Logs";
import { ENTERTAINMENT_LOG_CATEGORIES, LOG_ACTIONS, LOG_TYPES } from "../../../../utils/Constant/LogConstant";
import { getVessels } from "../../../../utils/ApiCalls/SpecialPage/index";
import {
  deleteEntertainmentRvc,
  getEntertainmentRvcs,
} from "../../../../utils/ApiCalls/BackOffice/Entertainment";

const PAGE_PATH = "/shore/back-office/entertainment/management";

const EntertainmentManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [{ permission, userDetails }, dispatch] = useDataLayerValue();
  const [topTab, setTopTab] = useState("management");

  const [vesselOptions, setVesselOptions] = useState([]);
  const [vesselId, setVesselId] = useState(() => {
    const urlVesselId = new URLSearchParams(location.search).get("vesselId");
    return urlVesselId ? Number(urlVesselId) : "";
  });

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 1000);

  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(location.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(location.search).get("pageSize")) || 10
  );
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Load vessels the current user has permission to view on this page.
  useEffect(() => {
    const loadVessels = async () => {
      const vessels = await getVessels();
      const allowed = (vessels || []).filter((item) =>
        checkPermission(
          permission,
          "read_per",
          "Management",
          `${PAGE_PATH}/${item.name.toLowerCase().replaceAll(" ", "-")}`
        )
      );
      setVesselOptions(allowed);

      const currentVesselId = new URLSearchParams(location.search).get("vesselId");
      const isCurrentAllowed = allowed.some((v) => String(v.id) === String(currentVesselId));
      if (!isCurrentAllowed && allowed.length) {
        setVesselId(allowed[0].id);
      }
    };
    loadVessels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission]);

  // Keep the URL in sync with vessel/page/search so the page is shareable/refreshable.
  useEffect(() => {
    const params = new URLSearchParams();
    if (vesselId) params.set("vesselId", vesselId);
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (params.toString() !== location.search.replace(/^\?/, "")) {
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselId, page, pageSize, search]);

  const loadRvcs = async (targetPage) => {
    if (!vesselId) return;
    const res = await getEntertainmentRvcs({
      vesselId,
      page: targetPage,
      limit: pageSize,
      search: debouncedSearch || undefined,
    });
    if (res?.error) return;
    setRows(res.data || []);
    setTotalCount(res.totalCount || 0);
  };

  useEffect(() => {
    if (!vesselId) return;
    loadRvcs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselId, page, pageSize, debouncedSearch]);

  const handleEdit = (id) => {
    if (checkPermission(permission, "update_per", "Management", window?.location?.pathname)) {
      navigate(`${PAGE_PATH}/edit/${id}`);
    } else {
      message.error("Update permission missing");
    }
  };

  const handleDelete = async (id) => {
    if (checkPermission(permission, "delete_per", "Management", window?.location?.pathname)) {
      const title = rows.find((row) => row.id === id)?.name;
      const data = await deleteEntertainmentRvc(id);
      if (data?.error) {
        dispatch({
          type: "SET_ERROR_MESSAGE",
          errMess: { message: data.error, type: "error" },
        });
        return;
      }
      addlog(
        LOG_ACTIONS.REMOVED,
        LOG_TYPES.ENTERTAINMENT_RVC,
        { id, title, category: ENTERTAINMENT_LOG_CATEGORIES.RVC },
        userDetails.id
      );
      loadRvcs(page);
    } else {
      message.error("Missing Permission for delete");
    }
  };

  const columns = [
    {
      title: "Sl",
      key: "sl",
      align: "center",
      width: 60,
      render: (value, row, index) => (page - 1) * pageSize + index + 1,
    },
    { title: "RVC ID", key: "rvcId", align: "center" },
    { title: "Name", key: "name" },
    { title: "Name (SC)", key: "nameSimplifiedChinese" },
    { title: "Name (TC)", key: "nameTraditionalChinese" },
    {
      title: "Action",
      key: "operation",
      align: "center",
      width: 100,
      render: (value, row) => (
        <div className="table_action_btn">
          <button className="btn" onClick={() => handleEdit(row.id)}>
            <i className="ri-edit-2-line" data-toggle="tooltip" title="Edit"></i>
          </button>
          <DeletePop onConfirm={() => handleDelete(row.id)}>
            <div>
              <button className="btn delete_btn">
                <i className="ri-delete-bin-line" data-toggle="tooltip" title="Delete"></i>
              </button>
            </div>
          </DeletePop>
        </div>
      ),
    },
  ];

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Onboard Purchase Management", route: PAGE_PATH },
  ];

  return (
    <Layout type={"read_per"} module_data={"Management"}>
      <div>
        <HeaderBread navigation={navigation} title={"Onboard Purchase Management"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Onboard Purchase Management", value: "management" },
              { title: "History Logs", value: "logs" },
            ]}
            activeTab={topTab}
            setActiveTab={setTopTab}
          />

          {topTab !== "management" ? (
            <Logs
              type={LOG_TYPES.ENTERTAINMENT_RVC}
              slug={`${PAGE_PATH}/edit/`}
              extendedColors
              removedClickable
            />
          ) : (
            <div className="card">
              <div className="card-header two_item">
                <h5 className="card-title">Revenue Centers</h5>
                <div className="two_item">
                  <Link
                    to={vesselId ? `${PAGE_PATH}/add?vesselId=${vesselId}` : `${PAGE_PATH}/add`}
                    className="btn add_v_btn mx-2"
                  >
                    Add Revenue Center
                  </Link>
                </div>
              </div>
              <div className="card-body">
                <div className="table_holder">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                    }}
                  >
                    <SwitchBar
                      tabs={vesselOptions.map((item) => ({
                        title: item.name,
                        value: item.id,
                      }))}
                      activeTab={vesselId}
                      setActiveTab={(val) => {
                        setVesselId(val);
                        setPage(1);
                      }}
                    />

                    <div style={{ maxWidth: 200 }}>
                      <SearchField
                        value={search}
                        setValue={(e) => setSearch(e.target.value)}
                        SearchValue={() => setPage(1)}
                        placeholder="Search"
                      />
                    </div>
                  </div>

                  <SortableTable
                    columns={columns}
                    rows={rows}
                    page={page}
                    setPage={setPage}
                    count={totalCount}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default EntertainmentManagement;
